import { Button, Icon, Input, Modal, Stack, toast } from "@assetwatch/ui";
import { useEffect, useRef, useState } from "react";
import { useDocumentCategories } from "../../../queries/documentCategories";
import {
  useCreateDocument,
  useDocumentUploadUrl,
} from "../../../queries/documents";
import {
  type AssetTreeNode,
  useAssetTreeQuery,
} from "../../../queries/useAssetTreeQuery";

interface UploadDocumentModalProps {
  customerId: string;
  facilityId: string;
  isOpen: boolean;
  onClose: () => void;
  onDocumentCreated: () => void;
}

// Primary category names for categorization
const PRIMARY_CATEGORY_NAMES = [
  "Manual",
  "Procedure",
  "Health & Safety",
  "Drawings",
  "Qualifications",
];

function flattenAssets(nodes: AssetTreeNode[]): Array<{
  assetId: number;
  assetName: string;
  assetNumber: string;
}> {
  return nodes.flatMap((node) => [
    {
      assetId: node.assetTreeId,
      assetName: node.name,
      assetNumber: node.code,
    },
    ...flattenAssets(node.children),
  ]);
}

export const UploadDocumentModal = ({
  customerId,
  facilityId,
  isOpen,
  onClose,
  onDocumentCreated,
}: UploadDocumentModalProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragOverRef = useRef<HTMLButtonElement>(null);

  const [documentName, setDocumentName] = useState("");
  const [status, setStatus] = useState<"Published" | "Unpublished">(
    "Unpublished",
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [primaryCategoryId, setPrimaryCategoryId] = useState<number | null>(
    null,
  );
  const [secondaryCategoryIds, setSecondaryCategoryIds] = useState<number[]>(
    [],
  );
  const [assetIds, setAssetIds] = useState<number[]>([]);
  const [assetSearchText, setAssetSearchText] = useState("");
  const [isDragOver, setIsDragOver] = useState(false);
  const createDocumentMutation = useCreateDocument();
  const documentUploadUrlMutation = useDocumentUploadUrl();
  const { data: assetTree } = useAssetTreeQuery();
  const assets = assetTree ? flattenAssets(assetTree.nodes) : [];

  const categoriesQuery = useDocumentCategories(customerId, facilityId, isOpen);
  const categoriesLoading = categoriesQuery.isPending;
  const primaryCategories = (categoriesQuery.data ?? []).filter((category) =>
    PRIMARY_CATEGORY_NAMES.includes(category.name),
  );
  const secondaryCategories = (categoriesQuery.data ?? []).filter(
    (category) => !PRIMARY_CATEGORY_NAMES.includes(category.name),
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: Reset selections whenever the owning scope or modal session changes.
  useEffect(() => {
    setPrimaryCategoryId(null);
    setSecondaryCategoryIds([]);
    setAssetIds([]);
  }, [customerId, facilityId, isOpen]);

  useEffect(() => {
    if (!isOpen || !categoriesQuery.data) return;
    const primary = categoriesQuery.data.filter((category) =>
      PRIMARY_CATEGORY_NAMES.includes(category.name),
    );
    setPrimaryCategoryId(
      primary.find((category) => category.name === "Manual")?.id ??
        primary[0]?.id ??
        null,
    );
  }, [isOpen, categoriesQuery.data]);

  const filteredAssets = assets.filter(
    (asset) =>
      asset.assetName.toLowerCase().includes(assetSearchText.toLowerCase()) ||
      asset.assetNumber.toLowerCase().includes(assetSearchText.toLowerCase()),
  );

  const handleFileSelected = (files: FileList | null) => {
    if (files && files.length > 0) {
      const file = files[0];
      // Validate file type
      const validTypes = [
        "application/pdf",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ];
      if (!validTypes.includes(file.type)) {
        toast.error("Please select a PDF, DOCX, or XLSX file");
        return;
      }
      // Validate file size (25MB)
      if (file.size > 25 * 1024 * 1024) {
        toast.error("File must be smaller than 25MB");
        return;
      }
      setSelectedFile(file);
      // Auto-fill document name from file name
      setDocumentName(file.name.replace(/\.[^/.]+$/, ""));
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    handleFileSelected(e.dataTransfer.files);
  };

  const handleSubmit = async () => {
    if (!selectedFile || !documentName || assetIds.length === 0) {
      toast.error(
        "Please select a file, enter a document name, and select at least one asset",
      );
      return;
    }

    try {
      const upload = await documentUploadUrlMutation.mutateAsync({
        customerId,
        facilityId,
        file: selectedFile,
        assetIds,
      });

      const uploadResponse = await fetch(upload.uploadUrl, {
        method: "PUT",
        headers: {
          "Content-Type": selectedFile.type,
        },
        body: selectedFile,
      });
      if (!uploadResponse.ok) {
        throw new Error(`S3 upload failed: ${uploadResponse.status}`);
      }

      const categoryIds = [primaryCategoryId, ...secondaryCategoryIds].filter(
        (categoryId): categoryId is number => categoryId !== null,
      );

      await createDocumentMutation.mutateAsync({
        facilityId,
        customerId,
        payload: {
          documentName,
          fileId: upload.fileId,
          status,
          categoryIds,
          assetIds: assetIds.length > 0 ? assetIds : undefined,
        },
      });

      // Reset form and close modal
      setDocumentName("");
      setStatus("Unpublished");
      setSelectedFile(null);
      setPrimaryCategoryId(1); // Reset to Manual
      setSecondaryCategoryIds([]);
      setAssetIds([]);
      setAssetSearchText("");
      toast.add("Document uploaded successfully", { icon: true });
      onDocumentCreated();
      onClose();
    } catch (error) {
      console.error("Failed to upload document:", error);
      toast.error("Failed to upload document");
    }
  };

  return (
    <Modal
      title="Upload document"
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            loading={
              createDocumentMutation.isPending ||
              documentUploadUrlMutation.isPending
            }
            disabled={!selectedFile || !documentName || assetIds.length === 0}
          >
            ✓ Upload
          </Button>
        </>
      }
    >
      <Stack gap={20}>
        {/* File Upload Drag & Drop */}
        <div className="flex flex-col gap-3">
          <button
            ref={dragOverRef}
            type="button"
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                fileInputRef.current?.click();
              }
            }}
            className={`flex flex-col items-center justify-center gap-4 rounded-lg border-2 border-dashed p-12 transition cursor-pointer ${
              isDragOver
                ? "border-primary bg-primary/5"
                : "border-outline-subtle bg-surface"
            }`}
          >
            <div className="text-text-subtle">
              <Icon name="document-upload" size="lg" />
            </div>
            <div className="text-center">
              <p className="font-medium text-text-default">
                Drop a file here, or choose one
              </p>
              <p className="text-xs text-text-subtle mt-1">
                PDF, DOCX, XLSX up to 25MB
              </p>
            </div>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.docx,.xlsx"
            onChange={(e) => handleFileSelected(e.target.files)}
            className="hidden"
          />
          <Input
            type="text"
            placeholder="File name (e.g., Bearing replacement SOP.pdf)"
            value={documentName}
            onChange={(e) => setDocumentName(e.target.value)}
          />
          {selectedFile && (
            <p className="text-xs text-text-subtle">
              Selected: {selectedFile.name} (
              {(selectedFile.size / 1024 / 1024).toFixed(1)} MB)
            </p>
          )}
        </div>

        {/* Classification Section */}
        <div className="flex flex-col gap-4">
          <h3 className="font-medium text-text-default">Classification</h3>

          {categoriesLoading ? (
            <p className="text-sm text-text-subtle">Loading categories...</p>
          ) : (
            <>
              {/* Primary categories */}
              <div className="flex flex-wrap gap-2">
                {primaryCategories.map((cat) => (
                  <button
                    type="button"
                    key={cat.id}
                    onClick={() =>
                      setPrimaryCategoryId(
                        primaryCategoryId === cat.id ? null : cat.id,
                      )
                    }
                    className={`rounded px-3 py-1.5 text-sm font-medium transition ${
                      primaryCategoryId === cat.id
                        ? "bg-text-default text-surface"
                        : "border border-border bg-surface hover:border-text-default"
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>

              {/* Secondary categories */}
              <div className="flex flex-wrap gap-2">
                {secondaryCategories.map((cat) => (
                  <button
                    type="button"
                    key={cat.id}
                    onClick={() => {
                      setSecondaryCategoryIds(
                        secondaryCategoryIds.includes(cat.id)
                          ? secondaryCategoryIds.filter((id) => id !== cat.id)
                          : [...secondaryCategoryIds, cat.id],
                      );
                    }}
                    className={`rounded px-3 py-1.5 text-sm font-medium transition ${
                      secondaryCategoryIds.includes(cat.id)
                        ? "bg-text-default text-surface"
                        : "border border-border bg-surface hover:border-text-default"
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Status */}
        <div className="flex flex-col gap-3">
          <span className="text-sm font-medium text-text-default">Status</span>
          <select
            value={status}
            onChange={(e) =>
              setStatus(e.target.value as "Published" | "Unpublished")
            }
            className="rounded border border-outline-subtle bg-surface px-3 py-2 text-sm text-text-default"
          >
            <option value="Unpublished">Unpublished</option>
            <option value="Published">Published</option>
          </select>
        </div>

        {/* Asset Association */}
        <div className="flex flex-col gap-3">
          <span className="text-sm font-medium text-text-default">
            Link to asset(s)
          </span>
          <Input
            type="text"
            placeholder="Search assets..."
            value={assetSearchText}
            onChange={(e) => setAssetSearchText(e.target.value)}
          />

          {/* Asset list with checkboxes */}
          <div className="max-h-48 overflow-y-auto rounded border border-outline-subtle bg-surface">
            {filteredAssets.length > 0 ? (
              <div className="flex flex-col">
                {filteredAssets.map((asset) => (
                  <label
                    key={asset.assetId}
                    className="flex cursor-pointer items-center gap-3 px-4 py-3 hover:bg-surface-container-lowest"
                  >
                    <input
                      type="checkbox"
                      checked={assetIds.includes(asset.assetId)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setAssetIds([...assetIds, asset.assetId]);
                        } else {
                          setAssetIds(
                            assetIds.filter((id) => id !== asset.assetId),
                          );
                        }
                      }}
                      className="h-4 w-4 shrink-0 rounded border-border"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-text-default">
                        {asset.assetName}
                      </p>
                    </div>
                    <span className="text-xs text-text-subtle shrink-0">
                      {asset.assetNumber}
                    </span>
                  </label>
                ))}
              </div>
            ) : assetSearchText ? (
              <p className="p-4 text-xs text-muted">No assets found</p>
            ) : (
              <div className="divide-y divide-border">
                {assets?.slice(0, 5).map((asset) => (
                  <label
                    key={asset.assetId}
                    className="flex cursor-pointer items-center gap-3 px-4 py-3 hover:bg-surface-container-lowest"
                  >
                    <input
                      type="checkbox"
                      checked={assetIds.includes(asset.assetId)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setAssetIds([...assetIds, asset.assetId]);
                        } else {
                          setAssetIds(
                            assetIds.filter((id) => id !== asset.assetId),
                          );
                        }
                      }}
                      className="h-4 w-4 shrink-0 rounded border-border"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-text-default">
                        {asset.assetName}
                      </p>
                    </div>
                    <span className="text-xs text-text-subtle shrink-0">
                      {asset.assetNumber}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
          {assetIds.length > 0 && (
            <p className="text-xs text-muted">
              {assetIds.length} asset(s) selected
            </p>
          )}
        </div>
      </Stack>
    </Modal>
  );
};
